import React, { ReactNode, useEffect, useState } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { theme } from './styles/theme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RecoilRoot } from 'recoil';
import TranslationProvider from './providers/TranslationProvider/TranslationProvider';
import { Toaster } from 'sonner';
import AuthProvider from './providers/AuthProvider/AuthProvider';
import HealthProvider from './providers/HealthProvider/HealthProvider';
import RouteProvider from './providers/RouteProvider/RouteProvider';
import {
  clearLocalStorageValue,
  getLocalStorageValue,
  LocalStorageKey,
  setLocalStorageValue,
} from './utils/localStorage/localStorage';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';
import createCache from '@emotion/cache';

export type CoreProps = {
  children?: ReactNode;
  routeProvider?: ReactNode;
};

const muiCache = createCache({
  key: 'mui',
  prepend: true,
});

const queryClient = new QueryClient();

export const Core = ({ children, routeProvider }: CoreProps) => {
  const [needsRestart] = useState(
    getLocalStorageValue(LocalStorageKey.needsRestart) ?? 0,
  );

  useEffect(() => {
    if (needsRestart && needsRestart > 0) {
      setLocalStorageValue(LocalStorageKey.needsRestart, needsRestart - 1);

      const timeoutRef = setTimeout(() => {
        window.location.reload();
      }, 300);

      return () => clearTimeout(timeoutRef);
    } else {
      clearLocalStorageValue(LocalStorageKey.needsRestart);
    }
  }, []);

  const handleDisplayShowNotification = (newVersion: string) => {
    setLocalStorageValue(LocalStorageKey.version, newVersion);
  };

  useEffect(() => {
    serviceWorkerRegistration.register({
      onSuccess: () => {},
      onUpdate: () => {},
    });
  }, []);

  return (
    <GoogleOAuthProvider
      clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ''}
    >
      <CacheProvider value={muiCache}>
        <MuiThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <RecoilRoot>
              <TranslationProvider>
                <Toaster />
                <AuthProvider>
                  <HealthProvider
                    onHasNewVersion={handleDisplayShowNotification}
                  >
                    {routeProvider ?? <RouteProvider />}
                    {children}
                  </HealthProvider>
                </AuthProvider>
              </TranslationProvider>
            </RecoilRoot>
          </QueryClientProvider>
        </MuiThemeProvider>
      </CacheProvider>
    </GoogleOAuthProvider>
  );
};

export default Core;
